import React from "react";
import { Field, Form, Formik, FormikProps } from "formik";
import { ScrollToFirstError } from "utils";
import {
  Button,
  Input,
  Textarea,
  SelectComponent,
  CalendarInput,
  DropFileInput,
  LargeSwitch,
  Warning,
} from "shared";
import {
  getTransactionFormConfig,
  getTransactionValidationSchema,
  getTransactionInitialValues,
  TransactionFormValues,
  TransactionFieldConfig,
  TransactionType,
} from "./forms";
import { useGetCountriesCurrency } from "hooks/countries";
import "./TransactionForm.scss";

interface TransactionFormProps {
  slug: string;
  defaultType?: TransactionType;
  onSubmit: (values: TransactionFormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
  maxQuantity?: number;
  warningText?: string;
  initialValues?: Partial<TransactionFormValues>;
  currencyCode?: string;
  existingFile?: { name: string; url: string } | null;
}

const renderField = (
  field: TransactionFieldConfig,
  formik: FormikProps<TransactionFormValues>,
  currencyOptions?: any[],
  existingFile?: { name: string; url: string } | null,
) => {
  const { name, label, type, placeholder } = field;

  const error =
    (formik.touched[name as keyof TransactionFormValues] ||
      formik.submitCount > 0) &&
    formik.errors[name as keyof TransactionFormValues]
      ? String(formik.errors[name as keyof TransactionFormValues])
      : undefined;

  switch (type) {
    case "switch":
      return (
        <div className="transaction-form__field" key={name}>
          <LargeSwitch
            state={String(
              formik.values[name as keyof TransactionFormValues] || "",
            )}
            options={field.options || []}
            onChange={(val: string) => formik.setFieldValue(name, val)}
          />
        </div>
      );

    case "text":
    case "number":
      return (
        <div className="transaction-form__field" key={name}>
          <Field
            as={Input}
            name={name}
            label={label}
            type={type}
            placeholder={placeholder}
            step={field.step}
            precision={field.precision}
            inputMode={field.inputMode}
            maxLength={field.maxLength}
            optional={field.required === false}
            error={error}
            data-suffix={field.suffix}
          />
        </div>
      );

    case "textarea":
      return (
        <div className="transaction-form__field" key={name}>
          <Field
            component={Textarea}
            name={name}
            label={label}
            placeholder={placeholder}
            optional={field.required === false}
            error={error}
          />
        </div>
      );

    case "select":
      return (
        <div className="transaction-form__field" key={name}>
          <Field
            as={SelectComponent}
            options={
              name === "currencyUuid" ? currencyOptions : field.options || []
            }
            name={name}
            label={label}
            isSearchable
            optional={field.required === false}
          />
        </div>
      );

    case "date":
      return (
        <div className="transaction-form__field" key={name}>
          <Field
            type="date"
            name={name}
            label={label}
            optional={field.required === false}
            component={CalendarInput}
            errors={formik.errors}
            yearMonthPicker={true}
          />
        </div>
      );

    case "file":
      return (
        <div className="transaction-form__field" key={name}>
          <Field
            label={label}
            name={name}
            id={name}
            as={DropFileInput}
            accept={field.accept ?? ".pdf,.doc,.docx,.jpg,.jpeg,.png"}
            optional
            description={field.description}
            existingFile={existingFile}
          />
        </div>
      );

    case "computed": {
      const computedValue = field.computeValues
        ? field.computeValues(formik.values)
        : "";
      return (
        <div
          className="transaction-form__field transaction-form__field--computed"
          key={name}
        >
          <div className="transaction-form__computed-label">{label}</div>
          <div className="transaction-form__computed-value">
            {computedValue || ""}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
};

const TransactionForm = ({
  slug,
  defaultType = "buy",
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = "Zatwierdź",
  maxQuantity,
  warningText,
  initialValues: initialValuesProp,
  currencyCode,
  existingFile,
}: TransactionFormProps) => {
  const config = getTransactionFormConfig(slug, defaultType, currencyCode);
  const validationSchema = getTransactionValidationSchema(slug);
  const initialValues = getTransactionInitialValues(
    defaultType,
    initialValuesProp,
  );
  const { data: currencyOptions } = useGetCountriesCurrency();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form className="transaction-form">
          <ScrollToFirstError />
          <div className="transaction-form__fields">
            {config.fields.map((field, index) => (
              <React.Fragment key={field.name}>
                {renderField(field, formik, currencyOptions, existingFile)}
                {index === 0 && maxQuantity !== undefined && warningText && (
                  <Warning
                    className={
                      Number(
                        slug === "currencies"
                          ? formik.values.totalValue
                          : formik.values.quantity,
                      ) > maxQuantity
                        ? ""
                        : "warning--gray"
                    }
                    text={warningText}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="transaction-form__actions">
            <Button
              type="button"
              onClick={onCancel}
              label="Anuluj"
              className="button--stroke button--rounded"
              disabled={isLoading}
            />
            <Button
              type="submit"
              onClick={() => {}}
              label={submitLabel}
              className="button--rounded button--black"
              isLoading={isLoading}
              disabled={
                isLoading ||
                (maxQuantity !== undefined &&
                  Number(
                    slug === "currencies"
                      ? formik.values.totalValue
                      : formik.values.quantity,
                  ) > maxQuantity)
              }
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default TransactionForm;
